import { useEffect, useState } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { apiFetch } from '../../lib/api';
import { SuburbField } from '../SuburbField';
import { today } from '../../lib/dates';
import type { Job } from '../../hooks/useJobs';

/**
 * New job dialog — the fields of the legacy jobs_form_view.php, in the dialog
 * style of click2gro's GeneralTaskEditDialog.
 *
 * Choosing a client shows its phone and address (read only, as the legacy form
 * did) and offers its contacts; picking one fills the site contact, which stays
 * editable. `job_map` is left out: it was empty on all 392 jobs and the column
 * was dropped in the jobs migration.
 */

const STATES = ['VIC', 'NSW', 'QLD', 'SA', 'WA', 'TAS', 'NT', 'ACT'] as const;

interface ClientContact {
    readonly id: number;
    readonly contactName: string;
    readonly jobTitle: string | null;
    readonly phoneNumber: string | null;
    readonly emailAddress: string | null;
}

interface ClientDetail {
    readonly client: {
        readonly phoneNumber: string | null;
        readonly streetName: string | null;
        readonly suburbName: string | null;
        readonly postcode: string | null;
        readonly state: string | null;
    };
    readonly contacts: readonly ClientContact[];
}

interface NewJobDialogProps {
    readonly open: boolean;
    readonly clients: readonly { id: number; companyName: string }[];
    readonly onCancel: () => void;
    readonly onCreated: (job: Job) => void;
}

function Section({ title, children }: { title: string; children: React.ReactNode }): React.JSX.Element {
    return (
        <Stack gap={1.5}>
            <Typography
                variant="subtitle2"
                sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5 }}
            >
                {title}
            </Typography>
            {children}
        </Stack>
    );
}

type Field =
    | 'jobRegisterDate'
    | 'jobNumber'
    | 'jobCode'
    | 'status'
    | 'budgetCost'
    | 'clientId'
    | 'jobAddress'
    | 'jobSuburb'
    | 'jobPostcode'
    | 'jobState'
    | 'contactName'
    | 'contactPhone'
    | 'contactEmail';

export function NewJobDialog({ open, clients, onCancel, onCreated }: NewJobDialogProps): React.JSX.Element {
    const [form, setForm] = useState<Record<Field, string>>({
        jobRegisterDate: today(),
        jobNumber: '',
        jobCode: '',
        status: 'Active',
        budgetCost: '',
        clientId: '',
        jobAddress: '',
        jobSuburb: '',
        jobPostcode: '',
        jobState: 'VIC',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
    });
    const [client, setClient] = useState<ClientDetail | null>(null);
    const [contactId, setContactId] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Pre-fill the next free number. It stays editable, and the server assigns
    // one anyway if the field is cleared.
    useEffect(() => {
        let cancelled = false;
        apiFetch<{ jobNumber: number }>('/jobs/next-number')
            .then((r) => {
                if (!cancelled) {
                    setForm((f) => (f.jobNumber === '' ? { ...f, jobNumber: String(r.jobNumber) } : f));
                }
            })
            .catch(() => {
                /* Left blank; the server assigns it. */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        setClient(null);
        setContactId('');
        if (form.clientId === '') {
            return;
        }
        let cancelled = false;
        apiFetch<ClientDetail>(`/clients/${form.clientId}`)
            .then((d) => {
                if (!cancelled) {
                    setClient(d);
                }
            })
            .catch(() => {
                /* Client details are informational; the job can still be created. */
            });
        return () => {
            cancelled = true;
        };
    }, [form.clientId]);

    const set = (key: Field) => (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm((f) => ({ ...f, [key]: e.target.value }));
    };

    function pickContact(id: string): void {
        setContactId(id);
        const c = client?.contacts.find((x) => String(x.id) === id);
        if (c !== undefined) {
            setForm((f) => ({
                ...f,
                contactName: c.contactName,
                contactPhone: c.phoneNumber ?? '',
                contactEmail: c.emailAddress ?? '',
            }));
        }
    }

    function create(): void {
        setSaving(true);
        setError(null);
        const text = (v: string): string | null => (v.trim() === '' ? null : v.trim());
        const num = (v: string): number | null => (v.trim() === '' ? null : Number(v));
        const body = {
            jobRegisterDate: text(form.jobRegisterDate),
            ...(form.jobNumber.trim() !== '' && { jobNumber: Number(form.jobNumber) }),
            jobCode: text(form.jobCode),
            status: form.status,
            budgetCost: num(form.budgetCost),
            clientId: num(form.clientId),
            jobAddress: text(form.jobAddress),
            jobSuburb: text(form.jobSuburb),
            jobPostcode: text(form.jobPostcode),
            jobState: text(form.jobState),
            contactName: text(form.contactName),
            contactPhone: text(form.contactPhone),
            contactEmail: text(form.contactEmail),
        };
        apiFetch<Job>('/jobs', { method: 'POST', body: JSON.stringify(body) })
            .then(onCreated)
            .catch((err: unknown) => {
                setError(err instanceof Error ? err.message : 'Could not create the job');
            })
            .finally(() => {
                setSaving(false);
            });
    }

    const clientAddress =
        client === null
            ? ''
            : [client.client.streetName, client.client.suburbName, client.client.state, client.client.postcode]
                  .filter((p) => p !== null && p !== '')
                  .join(', ');

    return (
        <Dialog
            open={open}
            onClose={saving ? undefined : onCancel}
            maxWidth="md"
            fullWidth
            slotProps={{ paper: { elevation: 1 } }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                Add Job
                <IconButton onClick={onCancel} size="small" disabled={saving} aria-label="Close">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent dividers>
                <Stack gap={3}>
                    {error !== null ? <Alert severity="error">{error}</Alert> : null}

                    <Section title="Job">
                        <Stack gap={1.5}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <TextField
                                    label="Registered date"
                                    type="date"
                                    size="small"
                                    fullWidth
                                    value={form.jobRegisterDate}
                                    onChange={set('jobRegisterDate')}
                                    slotProps={{ inputLabel: { shrink: true } }}
                                />
                                <TextField
                                    label="Job number"
                                    size="small"
                                    fullWidth
                                    value={form.jobNumber}
                                    onChange={set('jobNumber')}
                                    helperText="Next free number; change if needed"
                                />
                                <TextField
                                    label="Job code"
                                    size="small"
                                    fullWidth
                                    value={form.jobCode}
                                    onChange={set('jobCode')}
                                />
                            </Stack>
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <TextField
                                    label="Budget"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    value={form.budgetCost}
                                    onChange={set('budgetCost')}
                                    slotProps={{ htmlInput: { min: 0, step: '0.01' } }}
                                />
                                <TextField
                                    select
                                    label="Status"
                                    size="small"
                                    fullWidth
                                    value={form.status}
                                    onChange={set('status')}
                                >
                                    <MenuItem value="Active">Active</MenuItem>
                                    <MenuItem value="Inactive">Inactive</MenuItem>
                                </TextField>
                            </Stack>
                        </Stack>
                    </Section>

                    <Section title="Client">
                        <Stack gap={1.5}>
                            <TextField
                                select
                                label="Client"
                                size="small"
                                value={form.clientId}
                                onChange={set('clientId')}
                            >
                                <MenuItem value="">—</MenuItem>
                                {clients.map((c) => (
                                    <MenuItem key={c.id} value={String(c.id)}>
                                        {c.companyName}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {client !== null ? (
                                <>
                                    <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                        <TextField
                                            label="Phone number"
                                            size="small"
                                            fullWidth
                                            value={client.client.phoneNumber ?? ''}
                                            slotProps={{ input: { readOnly: true } }}
                                        />
                                        <TextField
                                            label="Address"
                                            size="small"
                                            fullWidth
                                            value={clientAddress}
                                            slotProps={{ input: { readOnly: true } }}
                                        />
                                    </Stack>
                                    <TextField
                                        select
                                        label="Contact"
                                        size="small"
                                        value={contactId}
                                        onChange={(e) => {
                                            pickContact(e.target.value);
                                        }}
                                        helperText={
                                            client.contacts.length === 0
                                                ? 'This client has no contacts'
                                                : 'Fills in the site contact below'
                                        }
                                        disabled={client.contacts.length === 0}
                                    >
                                        <MenuItem value="">—</MenuItem>
                                        {client.contacts.map((c) => (
                                            <MenuItem key={c.id} value={String(c.id)}>
                                                {c.contactName}
                                                {c.jobTitle !== null && c.jobTitle !== '' ? ` — ${c.jobTitle}` : ''}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </>
                            ) : form.clientId !== '' ? (
                                <CircularProgress size={18} />
                            ) : null}
                        </Stack>
                    </Section>

                    <Section title="Site">
                        <Stack gap={1.5}>
                            <TextField
                                label="Street"
                                size="small"
                                required
                                value={form.jobAddress}
                                onChange={set('jobAddress')}
                            />
                            <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                                <SuburbField
                                    value={form.jobSuburb}
                                    onChange={(v) => {
                                        setForm((f) => ({ ...f, jobSuburb: v }));
                                    }}
                                    onSelect={(m) => {
                                        setForm((f) => ({
                                            ...f,
                                            jobSuburb: m.suburb,
                                            jobPostcode: m.postcode,
                                            jobState: m.state,
                                        }));
                                    }}
                                />
                                <TextField
                                    label="Postcode"
                                    size="small"
                                    sx={{ width: { sm: 140 } }}
                                    value={form.jobPostcode}
                                    onChange={set('jobPostcode')}
                                    slotProps={{ htmlInput: { inputMode: 'numeric', maxLength: 4 } }}
                                />
                                <TextField
                                    select
                                    label="State"
                                    size="small"
                                    sx={{ width: { sm: 120 } }}
                                    value={form.jobState}
                                    onChange={set('jobState')}
                                >
                                    {STATES.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Stack>
                        </Stack>
                    </Section>

                    <Section title="Site contact">
                        <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5}>
                            <TextField
                                label="Contact name"
                                size="small"
                                fullWidth
                                value={form.contactName}
                                onChange={set('contactName')}
                            />
                            <TextField
                                label="Contact phone"
                                size="small"
                                fullWidth
                                value={form.contactPhone}
                                onChange={set('contactPhone')}
                            />
                            <TextField
                                label="Contact email"
                                size="small"
                                fullWidth
                                type="email"
                                value={form.contactEmail}
                                onChange={set('contactEmail')}
                            />
                        </Stack>
                    </Section>
                </Stack>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                {form.jobAddress.trim() === '' ? (
                    <Typography sx={{ fontSize: '0.8rem', color: 'text.secondary', mr: 'auto', pl: 1 }}>
                        A street address is required
                    </Typography>
                ) : null}
                <Button onClick={onCancel} disabled={saving}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    onClick={create}
                    disabled={saving || form.jobAddress.trim() === ''}
                    startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                >
                    {saving ? 'Saving...' : 'Save Job'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
